import { useState, useEffect } from "react";
import { Trash2, ArrowRight, TagIcon, X, Loader2, ChevronLeft, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { LeadTag, SalesFunnel } from "@/types/lead";
import { cn } from "@/lib/utils";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import {
  BULK_BATCH_SIZE,
  bulkProgressPercent,
  chunkArray,
  type BulkOperationProgress,
} from "@/lib/bulk-lead-operations";
import { assignLeadsToMemberInBatches } from "@/lib/bulk-assign-leads";

interface Stage {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface AssignableMember {
  id: string;
  display_name: string | null;
  profile: { full_name: string | null } | null;
}

interface BulkActionsBarProps {
  selectedCount: number;
  selectedIds: Set<string>;
  stages: Stage[];
  funnels?: SalesFunnel[];
  workspaceId?: string;
  activeFunnelId?: string | null;
  workspaceTags: LeadTag[];
  onComplete: () => void;
  onCancel: () => void;
  integrationById?: WorkspaceIntegrationsMap;
}

export function BulkActionsBar({
  selectedCount,
  selectedIds,
  stages,
  funnels = [],
  workspaceId,
  activeFunnelId,
  workspaceTags,
  onComplete,
  onCancel,
  integrationById,
}: BulkActionsBarProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<BulkOperationProgress | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [removeTagOpen, setRemoveTagOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [members, setMembers] = useState<AssignableMember[]>([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [funnelStages, setFunnelStages] = useState<Stage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);

  useEffect(() => {
    if (!moveOpen) {
      setSelectedFunnelId(null);
      setFunnelStages([]);
    }
  }, [moveOpen]);

  useEffect(() => {
    if (!assignOpen || !workspaceId || membersLoaded) return;
    let cancelled = false;
    setLoadingMembers(true);
    void supabase
      .from("workspace_members")
      .select("id, display_name, profile:profiles(full_name)")
      .eq("workspace_id", workspaceId)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error) setMembers((data as unknown as AssignableMember[]) || []);
        setMembersLoaded(true);
        setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assignOpen, workspaceId, membersLoaded]);

  if (selectedCount === 0) return null;

  const ids = Array.from(selectedIds);

  const runBatched = async <T,>(
    label: string,
    batches: T[][],
    handler: (chunk: T[]) => Promise<void>,
  ) => {
    const total = ids.length;
    let completed = 0;

    setProgress({ completed: 0, total, label });

    for (const chunk of batches) {
      await handler(chunk);
      completed += chunk.length;
      setProgress({ completed, total, label });
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const batches = chunkArray(ids, BULK_BATCH_SIZE);
      await runBatched("Excluindo leads", batches, async (chunk) => {
        const { error } = await supabase.from("leads").delete().in("id", chunk);
        if (error) throw error;
      });

      toast({
        title: "Leads excluídos",
        description: `${ids.length} leads foram excluídos com sucesso.`,
      });
      onComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao excluir leads",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleSelectFunnel = async (funnelId: string) => {
    if (funnelId === activeFunnelId) {
      setSelectedFunnelId(funnelId);
      setFunnelStages(stages);
      return;
    }

    setSelectedFunnelId(funnelId);
    setLoadingStages(true);
    try {
      const { data, error } = await supabase
        .from("lead_stages")
        .select("id, name, slug, color")
        .eq("funnel_id", funnelId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setFunnelStages(data || []);
    } catch {
      setFunnelStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  const handleMoveStage = async (stageSlug: string, targetFunnelId?: string) => {
    setLoading(true);
    setMoveOpen(false);
    try {
      const isCrossFunnel =
        Boolean(targetFunnelId && activeFunnelId && targetFunnelId !== activeFunnelId);
      const batches = chunkArray(ids, BULK_BATCH_SIZE);

      if (isCrossFunnel) {
        if (!workspaceId || !activeFunnelId) {
          throw new Error("Workspace ou funil ativo não definido.");
        }

        await runBatched("Movendo leads", batches, async (chunk) => {
          const { error } = await supabase.rpc("bulk_move_leads_to_funnel", {
            p_workspace_id: workspaceId,
            p_source_funnel_id: activeFunnelId,
            p_target_funnel_id: targetFunnelId!,
            p_target_stage: stageSlug,
            p_lead_ids: chunk,
          });
          if (error) throw error;
        });
      } else {
        if (!workspaceId) {
          throw new Error("Workspace não definido.");
        }

        await runBatched("Movendo leads", batches, async (chunk) => {
          const { error } = await supabase.rpc("bulk_move_leads_to_stage", {
            p_workspace_id: workspaceId,
            p_funnel_id: activeFunnelId ?? null,
            p_stage: stageSlug,
            p_lead_ids: chunk,
          });
          if (error) throw error;
        });
      }

      const targetFunnel = funnels.find((f) => f.id === targetFunnelId);
      const stageName = (targetFunnelId === activeFunnelId ? stages : funnelStages)
        .find((s) => s.slug === stageSlug)?.name || stageSlug;

      const description = targetFunnelId && targetFunnelId !== activeFunnelId
        ? `${ids.length} leads movidos para "${stageName}" no funil "${targetFunnel?.name}".`
        : `${ids.length} leads movidos para "${stageName}".`;

      toast({ title: "Leads movidos", description });
      onComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao mover leads",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleAddTag = async (tagId: string) => {
    setLoading(true);
    setAddTagOpen(false);
    try {
      const inserts = ids.map((leadId) => ({
        lead_id: leadId,
        tag_id: tagId,
      }));
      const batches = chunkArray(inserts, BULK_BATCH_SIZE);

      await runBatched("Adicionando tag", batches, async (chunk) => {
        const { error } = await supabase
          .from("lead_tag_assignments")
          .upsert(chunk, { onConflict: "lead_id,tag_id", ignoreDuplicates: true });
        if (error) throw error;
      });

      const tagName = workspaceTags.find((t) => t.id === tagId)?.name || "";
      toast({
        title: "Tag adicionada",
        description: `Tag "${tagName}" adicionada a ${ids.length} leads.`,
      });
      onComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao adicionar tag",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    setLoading(true);
    setRemoveTagOpen(false);
    try {
      const batches = chunkArray(ids, BULK_BATCH_SIZE);

      await runBatched("Removendo tag", batches, async (chunk) => {
        const { error } = await supabase
          .from("lead_tag_assignments")
          .delete()
          .in("lead_id", chunk)
          .eq("tag_id", tagId);
        if (error) throw error;
      });

      const tagName = workspaceTags.find((t) => t.id === tagId)?.name || "";
      toast({
        title: "Tag removida",
        description: `Tag "${tagName}" removida de ${ids.length} leads.`,
      });
      onComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao remover tag",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const memberLabel = (member: AssignableMember) =>
    (member.display_name || member.profile?.full_name || "Membro").trim();

  const handleAssignMember = async (memberId: string) => {
    if (!workspaceId) {
      toast({
        title: "Erro ao atribuir",
        description: "Workspace não definido.",
        variant: "destructive",
      });
      return;
    }

    const label = memberLabel(members.find((member) => member.id === memberId) ?? {
      id: memberId,
      display_name: null,
      profile: null,
    });

    setLoading(true);
    setAssignOpen(false);
    try {
      const { assigned, skipped } = await assignLeadsToMemberInBatches(
        workspaceId,
        ids,
        memberId,
        setProgress,
      );

      if (assigned === 0) {
        toast({
          title: "Nenhum lead atribuído",
          description:
            skipped > 0
              ? "Você não tem permissão para atribuir os leads selecionados."
              : "Nenhum lead foi atualizado.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Leads atribuídos",
        description:
          skipped > 0
            ? `${assigned} lead${assigned !== 1 ? "s" : ""} atribuído${assigned !== 1 ? "s" : ""} a ${label}. ${skipped} sem permissão.`
            : `${assigned} lead${assigned !== 1 ? "s" : ""} atribuído${assigned !== 1 ? "s" : ""} a ${label}.`,
      });
      onComplete();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao atribuir",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };
  const hasFunnels = funnels.length > 1;
  const showProgress = loading && progress && progress.total > 0;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="flex flex-col gap-2 px-4 py-3 bg-background/95 backdrop-blur-md border rounded-xl shadow-lg min-w-[min(100vw-2rem,520px)]">
        {showProgress && (
          <div className="space-y-1.5 pb-1 border-b">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground truncate">
                {progress.label}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums shrink-0">
                {progress.completed} / {progress.total}
              </p>
            </div>
            <Progress value={bulkProgressPercent(progress)} className="h-1.5" />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap justify-center">
          {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />}

          <Badge variant="secondary" className="text-sm font-medium">
            {selectedCount} selecionado{selectedCount !== 1 ? "s" : ""}
          </Badge>

          <div className="w-px h-6 bg-border mx-1 hidden sm:block" />

          <Popover open={moveOpen} onOpenChange={setMoveOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading}>
                <ArrowRight className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Mover</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2" align="center" side="top">
              {hasFunnels && !selectedFunnelId ? (
                <>
                  <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Mover para funil</p>
                  <div className="space-y-0.5 max-h-60 overflow-y-auto">
                    {funnels.map((funnel) => (
                      <button
                        key={funnel.id}
                        onClick={() => handleSelectFunnel(funnel.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                      >
                        <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", funnel.color)} />
                        <span className="truncate flex-1">
                          {resolveFunnelDisplayName(funnel, integrationById)}
                        </span>
                        {funnel.id === activeFunnelId && (
                          <span className="text-[10px] text-muted-foreground">atual</span>
                        )}
                        <ChevronLeft className="w-3 h-3 text-muted-foreground rotate-180" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {hasFunnels && selectedFunnelId && (
                    <button
                      onClick={() => { setSelectedFunnelId(null); setFunnelStages([]); }}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 pb-2 transition-colors"
                    >
                      <ChevronLeft className="w-3 h-3" />
                      Voltar aos funis
                    </button>
                  )}
                  <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Mover para estágio</p>
                  {loadingStages ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-0.5 max-h-60 overflow-y-auto">
                      {(selectedFunnelId ? funnelStages : stages).map((stage) => (
                        <button
                          key={stage.id}
                          onClick={() => handleMoveStage(stage.slug, selectedFunnelId || activeFunnelId || undefined)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                        >
                          <div className={`w-2.5 h-2.5 rounded-full ${stage.color} shrink-0`} />
                          <span className="truncate">{stage.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </PopoverContent>
          </Popover>

          <Popover open={assignOpen} onOpenChange={setAssignOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading || !workspaceId}>
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Atribuir</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="center" side="top">
              <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Atribuir a</p>
              {loadingMembers ? (
                <div className="flex justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : members.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Nenhum membro</p>
              ) : (
                <div className="space-y-0.5 max-h-60 overflow-y-auto">
                  {members.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => void handleAssignMember(member.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                    >
                      <span className="truncate">{memberLabel(member)}</span>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Popover open={addTagOpen} onOpenChange={setAddTagOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading}>
                <TagIcon className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">+ Tag</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="center" side="top">
              <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Adicionar tag</p>
              {workspaceTags.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Nenhuma tag criada</p>
              ) : (
                <div className="space-y-0.5 max-h-60 overflow-y-auto">
                  {workspaceTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleAddTag(tag.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${tag.color} shrink-0`} />
                      <span className="truncate">{tag.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Popover open={removeTagOpen} onOpenChange={setRemoveTagOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={loading}>
                <TagIcon className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">- Tag</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2" align="center" side="top">
              <p className="text-xs font-medium text-muted-foreground px-2 pb-2">Remover tag</p>
              {workspaceTags.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Nenhuma tag criada</p>
              ) : (
                <div className="space-y-0.5 max-h-60 overflow-y-auto">
                  {workspaceTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleRemoveTag(tag.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted text-left"
                    >
                      <div className={`w-2.5 h-2.5 rounded-full ${tag.color} shrink-0`} />
                      <span className="truncate">{tag.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={loading}>
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Excluir</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir {selectedCount} lead{selectedCount !== 1 ? "s" : ""}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita. Todos os dados associados a esses leads serão removidos permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="w-px h-6 bg-border mx-1 hidden sm:block" />

          <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
            <X className="w-4 h-4" />
            <span className="hidden sm:inline ml-1.5">Cancelar</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
